import { Link } from 'react-router-dom';
import { 
  Award, 
  Heart, 
  MapPin, 
  Calendar,
  CheckCircle,
  Quote,
  CalendarCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { JANE_BOOKING_URL } from '../data/janeServices';

const PRACTITIONER_IMAGE = "https://customer-assets.emergentagent.com/job_266cf7bd-85bf-4fa7-83fe-d09732430dba/artifacts/qe0wfozn_Screenshot%202026-02-16%20at%209.18.36%E2%80%AFPM.png";
const LOCATION_IMAGE = "https://images.unsplash.com/photo-1740990556828-5af6b0e319a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxmbG9yaWRhJTIwa2V5cyUyMGNvYXN0YWwlMjBjYWxtJTIwd2F0ZXJ8ZW58MHx8fHwxNzcxNDYxOTIxfDA&ixlib=rb-4.1.0&q=85";

const AboutPage = () => {
  const credentials = [
    {
      icon: Award,
      title: "25+ Years Experience",
      description: "Dedicated to maternal-infant care"
    },
    {
      icon: Heart,
      title: "Certified Lactation Consultant",
      description: "IBCLC certified professional"
    },
    {
      icon: Calendar,
      title: "Childbirth Educator",
      description: "Comprehensive birth preparation"
    },
    {
      icon: CheckCircle,
      title: "Advanced Training",
      description: "Postpartum & pelvic floor health"
    }
  ];

  return (
    <div data-testid="about-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-muted">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="space-y-6">
              <p className="text-sm tracking-widest uppercase font-semibold text-primary">
                About Me
              </p>
              <h1 className="text-5xl md:text-6xl font-medium tracking-tight text-foreground">
                A Lifetime Dedicated to Mothers & Babies
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Supporting women through pregnancy, birth, and postpartum has truly 
                been my life's passion.
              </p>
            </div>

            {/* Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-secondary/30 rounded-3xl transform -rotate-3" />
              <img 
                src={PRACTITIONER_IMAGE} 
                alt="Healthcare practitioner" 
                className="relative rounded-3xl w-full h-96 object-cover shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Bio Section */}
      <section data-testid="bio-section" className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-border/50">
            <Quote className="w-12 h-12 text-secondary mb-6" strokeWidth={1} />
            
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>
                I have spent more than 25 years caring for mothers and babies, and supporting 
                women through pregnancy, birth, and postpartum has truly been my life's passion. 
                Early in my career, I discovered how meaningful it was to be part of the labor 
                and delivery experience, which led me to spend over two decades at Baptist Health 
                in Miami helping welcome countless babies into the world. Being trusted during 
                such an important moment in a family's life is something I have never taken for granted.
              </p>
              
              <p>
                After raising my three children here in the Florida Keys, I felt a strong calling 
                to continue this work within my own community. Teaching childbirth education has 
                become one of the most rewarding parts of what I do. I believe both moms and dads 
                deserve to feel comfortable, supported, and confident throughout the childbirth 
                process and in every decision along the way.
              </p>
              
              <p>
                In addition to childbirth education, I am a certified lactation consultant and 
                have completed advanced training in postpartum care and pelvic floor health. 
                My goal is to provide compassionate, personalized guidance so that every woman 
                feels heard, informed, and supported.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials Grid */}
      <section data-testid="credentials-section" className="py-24 md:py-32 bg-muted">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-sm tracking-widest uppercase font-semibold text-primary mb-4">
              Qualifications
            </p>
            <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-foreground">
              Credentials & Experience
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {credentials.map((cred) => (
              <div
                key={cred.title}
                data-testid={`credential-${cred.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="bg-white p-8 rounded-3xl text-center shadow-sm hover:shadow-lg transition-all duration-300"
              >
                <div className="w-16 h-16 bg-secondary/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <cred.icon className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-medium text-foreground mb-2">
                  {cred.title}
                </h3>
                <p className="text-muted-foreground">
                  {cred.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section data-testid="location-section" className="py-24 md:py-32 bg-muted">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <img 
                src={LOCATION_IMAGE} 
                alt="Florida Keys" 
                className="rounded-3xl w-full h-80 object-cover shadow-xl"
              />
            </div>

            {/* Content */}
            <div className="space-y-6 order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-secondary/20 text-secondary-foreground px-4 py-2 rounded-full text-sm">
                <MapPin className="w-4 h-4" />
                <span>Florida Keys</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-foreground">
                Serving Our Community
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                After years of serving families in Miami, I'm proud to bring my expertise 
                and passion to the beautiful Florida Keys community where I raised my own 
                family. There's something special about supporting mothers and families 
                in this unique, close-knit community.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Whether you're a first-time parent or adding to your growing family, 
                I'm here to provide the personalized, compassionate care you deserve.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-medium tracking-tight mb-6">
            Let's Work Together
          </h2>
          <p className="text-base md:text-lg text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
            I would be honored to be part of your journey. Book your appointment
            online whenever you are ready.
          </p>
          <a
            href={JANE_BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-book-online-button"
          >
            <Button 
              className="bg-white text-primary hover:bg-white/90 px-8 py-6 rounded-full text-base font-medium transition-transform hover:-translate-y-1"
            >
              <CalendarCheck className="w-5 h-5 mr-2" />
              Book Online
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
